/** Quo SMS settings (v1 expenses-calendar-materials.js dialpadPanel — "Quo SMS
 * Settings"). A Setup Wizard over the SAME Quo backend the Dialer already reads
 * (/api/quo/status, /api/quo/register-webhook via dialer/api.ts) — no new
 * backend. Secrets (QUO_API_KEY, QUO_WEBHOOK_SECRET) live server-side, so there
 * is NO in-app secret input: Step 1 shows the API-key connection status; Step 2
 * shows the webhook-secret status + inbound endpoints and can (re)register the
 * webhooks. Reading status/numbers is live and safe; registering fires only on
 * an explicit click and only once QUO_API_KEY is set. The SMS/Calling tabs are
 * honest read-only summaries — no fake toggles that don't persist. Graceful when
 * the backend is unreachable or not configured. */
import { useEffect, useState } from "react";
import { fetchQuoStatus, fetchQuoNumbers, registerQuoWebhooks, type QuoStatus, type QuoNumber } from "../dialer/api";
import { BackendError } from "../../lib/backend";
import { API_BASE } from "../../config";

type QuoTab = "wizard" | "sms" | "calling";

const QUO_TABS: { id: QuoTab; label: string }[] = [
  { id: "wizard", label: "Setup Wizard" },
  { id: "sms", label: "SMS Settings" },
  { id: "calling", label: "Calling Settings" },
];

/** v1 dpSet/dpToggle field set (expenses-calendar-materials.js dialpadPanel),
 * verbatim: strings hours/signature/optOut/fromNumber/callerId, toggles
 * aiReply/recording/vmDrop, number ringTimeout, templates [{name,body}].
 * Whole-object DIALPAD save through the door (Phase 29) — SPREAD the existing
 * blob so server-managed flags (apiKeySet/secretSet/smsHook/…) never drop. */
const dstr = (v: unknown): string => (typeof v === "string" ? v : "");
const dbool = (v: unknown): boolean => v === true;
const dnum = (v: unknown, fb: number): number => (typeof v === "number" && Number.isFinite(v) ? v : fb);

export interface DpTemplate { name: string; body: string }
export function narrowDpTemplates(v: unknown): DpTemplate[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((t): t is Record<string, unknown> => t !== null && typeof t === "object" && !Array.isArray(t))
    .map((t) => ({ name: dstr(t["name"]), body: dstr(t["body"]) }));
}

export function QuoSms({ token, dialpad, canEdit, onPutDialpad }: {
  token: string | null;
  /** the org DIALPAD settings blob (server flags + editable prefs) */
  dialpad: unknown;
  canEdit: boolean;
  /** whole-object DIALPAD put (caller merges into orgSettings + outbox) */
  onPutDialpad?: (next: Record<string, unknown>) => void;
}) {
  const [tab, setTab] = useState<QuoTab>("wizard");
  const [status, setStatus] = useState<QuoStatus | null>(null);
  const [numbers, setNumbers] = useState<QuoNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [regBusy, setRegBusy] = useState(false);
  const [regNote, setRegNote] = useState<string | null>(null);

  // ---- editable DIALPAD prefs (v1 parity, item #4) --------------------------
  const D = dialpad !== null && typeof dialpad === "object" && !Array.isArray(dialpad) ? (dialpad as Record<string, unknown>) : {};
  const editable = canEdit && !!onPutDialpad;
  const dpPut = (patch: Record<string, unknown>) => { if (editable) onPutDialpad!({ ...D, ...patch }); };
  // draft strings commit on blur/Enter so each keystroke isn't a door write
  const [dpDraft, setDpDraft] = useState<Record<string, string>>({});
  const dval = (k: string) => (k in dpDraft ? dpDraft[k] ?? "" : dstr(D[k]));
  const commitStr = (k: string) => {
    if (!(k in dpDraft)) return;
    const v = (dpDraft[k] ?? "").trim();
    setDpDraft((d) => { const n = { ...d }; delete n[k]; return n; });
    if (v !== dstr(D[k])) dpPut({ [k]: v });
  };
  const tpls = narrowDpTemplates(D["templates"]);
  const [tplEdit, setTplEdit] = useState<number | null>(null);
  const [tplDraft, setTplDraft] = useState<DpTemplate>({ name: "", body: "" });
  const [tplDelConfirm, setTplDelConfirm] = useState<number | null>(null);
  const saveTpl = () => {
    if (tplEdit === null || !tplDraft.name.trim()) return;
    const next = [...tpls];
    next[tplEdit] = { name: tplDraft.name.trim(), body: tplDraft.body };
    dpPut({ templates: next });
    setTplEdit(null);
  };
  const addTpl = () => {
    dpPut({ templates: [...tpls, { name: "New template", body: "" }] });
    setTplEdit(tpls.length);
    setTplDraft({ name: "New template", body: "" });
  };
  const delTpl = (i: number) => {
    dpPut({ templates: tpls.filter((_, x) => x !== i) });
    setTplDelConfirm(null);
    if (tplEdit === i) setTplEdit(null);
  };

  const load = () => {
    setLoading(true);
    setErr(null);
    void Promise.all([
      fetchQuoStatus(token, undefined),
      fetchQuoNumbers(token, undefined).catch(() => [] as QuoNumber[]),
    ])
      .then(([st, nums]) => { setStatus(st); setNumbers(nums); })
      .catch((e) => setErr(e instanceof BackendError ? e.detail : "Could not reach the backend"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const apiKeySet = status?.apiKeySet === true;
  const secretSet = status?.webhookSecretSet === true;
  const endpointsReady = apiKeySet && secretSet;

  const register = async () => {
    if (!apiKeySet || regBusy) return;
    setRegBusy(true);
    setRegNote(null);
    try {
      const r = await registerQuoWebhooks(token);
      setRegNote(r.ok
        ? "Inbound webhooks registered ✓ — incoming texts & calls will log automatically."
        : "Registration ran, but the server reported: " + JSON.stringify(r.raw).slice(0, 140));
      load();
    } catch (e) {
      setRegNote(e instanceof BackendError ? "Registration failed: " + e.detail : "Registration failed — check QUO_API_KEY on the server.");
    } finally {
      setRegBusy(false);
    }
  };

  return (
    <div className="setsec quoset">
      <div className="setuserhd">
        <h3>Quo SMS</h3>
        <button className="btn slim" onClick={load} disabled={loading}>{loading ? "Checking…" : "Re-check"}</button>
      </div>
      <div className="setgate">Text &amp; call over your Quo line. Connection status is read live; secrets (<code>QUO_API_KEY</code>, <code>QUO_WEBHOOK_SECRET</code>) stay server-side and are never shown in-app.</div>

      {err ? (
        <div className="setcard">
          <div className="setcardhd"><strong>Quo isn't reachable right now</strong><span className="setintstatus off">Offline</span></div>
          <p className="sub">{err}</p>
          <p className="sub setnote">Quo SMS connects through the backend — set <code>QUO_API_KEY</code> and the webhook secret (<code>QUO_WEBHOOK_SECRET</code>) as server secrets, then reconnect. Nothing here is stored in the browser.</p>
          <div className="setrow"><button className="btn slim primary" onClick={load} disabled={loading}>{loading ? "Retrying…" : "Retry"}</button></div>
        </div>
      ) : loading && !status ? (
        <div className="setcard"><p className="sub">Checking your Quo connection…</p></div>
      ) : (
        <>
          <nav className="settingstabs quotabs">
            {QUO_TABS.map((t) => (
              <button key={t.id} className={"settingstab" + (tab === t.id ? " on" : "")} onClick={() => setTab(t.id)}>{t.label}</button>
            ))}
          </nav>

          {tab === "wizard" ? (
            <div className="quowiz">
              {/* Step 1 — API key (server secret; status only, no in-app input) */}
              <div className="setcard">
                <div className="setcardhd">
                  <strong>Step 1 — Quo API Key</strong>
                  <span className={"setintstatus " + (apiKeySet ? "on" : "off")}>{apiKeySet ? "✓ Connected" : "Not configured"}</span>
                </div>
                <div className="setintegration">
                  <div className="setintwho">
                    <strong>API key</strong>
                    <span className="sub">Stored server-side as the <code>QUO_API_KEY</code> secret — never shown in the app.</span>
                  </div>
                  <span className={"setintstatus " + (apiKeySet ? "on" : "off")}>{apiKeySet ? "Set" : "Not set"}</span>
                </div>
                {apiKeySet
                  ? <p className="sub setnote">Connected. To rotate the key, update the <code>QUO_API_KEY</code> secret on the server, then press Re-check.</p>
                  : <div className="setgate warn">The <code>QUO_API_KEY</code> secret isn't set yet. Add it in the app's server secrets, then press Re-check — there's no in-app key field, secrets stay on the backend.</div>}
              </div>

              {/* Step 2 — webhook secret + endpoints */}
              <div className="setcard">
                <div className="setcardhd">
                  <strong>Step 2 — Webhook Secret &amp; Endpoints</strong>
                  <span className={"setintstatus " + (secretSet ? "on" : "off")}>{secretSet ? "Secret set" : "Secret missing"}</span>
                </div>
                <div className="setintegration">
                  <div className="setintwho">
                    <strong>Webhook signing secret</strong>
                    <span className="sub">Verifies Quo callbacks. Stored server-side as <code>QUO_WEBHOOK_SECRET</code>.</span>
                  </div>
                  <span className={"setintstatus " + (secretSet ? "on" : "off")}>{secretSet ? "Set" : "Not set"}</span>
                </div>

                <div className="quoends">
                  <div className="quoend">
                    <span className="quoendlbl">SMS webhook</span>
                    <span className="quoendurl">{API_BASE}</span>
                    <span className={"setintstatus " + (endpointsReady ? "on" : "muted")}>{endpointsReady ? "Ready" : "Pending"}</span>
                  </div>
                  <div className="quoend">
                    <span className="quoendlbl">Calling webhook</span>
                    <span className="quoendurl">{API_BASE}</span>
                    <span className={"setintstatus " + (endpointsReady ? "on" : "muted")}>{endpointsReady ? "Ready" : "Pending"}</span>
                  </div>
                </div>
                <p className="sub">Inbound texts and calls are received by the backend above and registered with Quo on demand. The server owns the exact receiver routes; the signing secret verifies every callback.</p>

                <div className="setrow">
                  <button className="btn slim" disabled title="The webhook secret is generated and stored as the QUO_WEBHOOK_SECRET server secret during the supervised go-live — there's no in-app generator.">Generate Webhook Secret</button>
                  <button className="btn slim primary" onClick={register} disabled={!apiKeySet || regBusy} title={apiKeySet ? "Register the inbound SMS & call webhooks with Quo" : "Set QUO_API_KEY on the server first — registration needs the Quo API key."}>{regBusy ? "Registering…" : "Register Webhooks"}</button>
                </div>
                {!apiKeySet
                  ? <p className="sub">Registration needs the Quo API key — set <code>QUO_API_KEY</code> first.</p>
                  : !secretSet
                  ? <p className="sub">Tip: set <code>QUO_WEBHOOK_SECRET</code> too, so incoming callbacks are verified.</p>
                  : null}
                {regNote ? <div className="inbxnote">{regNote}</div> : null}
              </div>
            </div>
          ) : null}

          {tab === "sms" ? (
            <div className="setcard">
              <div className="setcardhd"><strong>SMS settings</strong><span className={"setintstatus " + (editable ? "on" : "muted")}>{editable ? "Editable" : "Read-only"}</span></div>
              <p className="sub setnote">v1's Quo SMS panel, live: these save org-wide and both apps read them. Text fields save when you click away (or press Enter).</p>
              <div className="setgrid dpgrid">
                <label className="setroifield"><span className="setroilabel">From number</span>
                  <input className="setroiinput" disabled={!editable} placeholder="+1 (___) ___-____" value={dval("fromNumber")} onChange={(e) => setDpDraft((d) => ({ ...d, fromNumber: e.target.value }))} onBlur={() => commitStr("fromNumber")} onKeyDown={(e) => { if (e.key === "Enter") commitStr("fromNumber"); }} />
                </label>
                <label className="setroifield"><span className="setroilabel">Business hours</span>
                  <input className="setroiinput" disabled={!editable} placeholder="9:00 AM – 7:00 PM" value={dval("hours")} onChange={(e) => setDpDraft((d) => ({ ...d, hours: e.target.value }))} onBlur={() => commitStr("hours")} onKeyDown={(e) => { if (e.key === "Enter") commitStr("hours"); }} />
                </label>
                <label className="setroifield wide"><span className="setroilabel">Default signature</span>
                  <input className="setroiinput" disabled={!editable} placeholder="— Tom, ETO Property Group" value={dval("signature")} onChange={(e) => setDpDraft((d) => ({ ...d, signature: e.target.value }))} onBlur={() => commitStr("signature")} onKeyDown={(e) => { if (e.key === "Enter") commitStr("signature"); }} />
                </label>
                <label className="setroifield"><span className="setroilabel">Opt-out keyword</span>
                  <input className="setroiinput" disabled={!editable} placeholder="STOP" value={dval("optOut")} onChange={(e) => setDpDraft((d) => ({ ...d, optOut: e.target.value }))} onBlur={() => commitStr("optOut")} onKeyDown={(e) => { if (e.key === "Enter") commitStr("optOut"); }} />
                </label>
              </div>
              <div className="setintegration">
                <div className="setintwho"><strong>AI auto-reply</strong><span className="sub">Let the AI draft first-touch replies to inbound texts (the Text Agent manages the drafts).</span></div>
                <button className={"dptoggle" + (dbool(D["aiReply"]) ? " on" : "")} disabled={!editable} onClick={() => dpPut({ aiReply: !dbool(D["aiReply"]) })}>{dbool(D["aiReply"]) ? "On" : "Off"}</button>
              </div>
              <SummaryRow label="Sending numbers" value={numbers.length ? `${numbers.length} Quo number${numbers.length === 1 ? "" : "s"} connected.` : "None found yet — connect once QUO_API_KEY is set."} />

              <div className="setcardhd dptplhd"><strong>SMS templates</strong>{editable ? <button className="btn slim" onClick={addTpl}>+ Add template</button> : null}</div>
              <p className="sub">Merge fields: <code>{"{name}"}</code> <code>{"{address}"}</code> <code>{"{price}"}</code> <code>{"{me}"}</code> — filled from the lead when picked on the Dialer.</p>
              {tpls.length === 0 ? <p className="sub">No templates yet.</p> : (
                <ul className="setlist dptpllist">
                  {tpls.map((t, i) => (
                    <li key={i}>
                      {tplEdit === i ? (
                        <div className="dptpledit">
                          <input value={tplDraft.name} placeholder="Template name" onChange={(e) => setTplDraft((d) => ({ ...d, name: e.target.value }))} />
                          <textarea value={tplDraft.body} rows={3} placeholder="Hi {name}, about {address}…" onChange={(e) => setTplDraft((d) => ({ ...d, body: e.target.value }))} />
                          <div className="esignsendacts">
                            <button className="btn slim primary" onClick={saveTpl}>Save</button>
                            <button className="btn slim" onClick={() => setTplEdit(null)}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <span className="esigntplname">{t.name || `Template ${i + 1}`}<em className="sub"> · {t.body ? t.body.slice(0, 48) + (t.body.length > 48 ? "…" : "") : "empty"}</em></span>
                          {editable ? (
                            <span className="esigntplacts">
                              <button className="btn xs" onClick={() => { setTplEdit(i); setTplDraft(tpls[i] ?? { name: "", body: "" }); }}>✎</button>
                              {tplDelConfirm === i
                                ? <button className="sowdel confirm" onClick={() => delTpl(i)}>sure?</button>
                                : <button className="sowdel" onClick={() => setTplDelConfirm(i)}>×</button>}
                            </span>
                          ) : null}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {tab === "calling" ? (
            <div className="setcard">
              <div className="setcardhd"><strong>Calling settings</strong><span className={"setintstatus " + (editable ? "on" : "muted")}>{editable ? "Editable" : "Read-only"}</span></div>
              <div className="quocallerids">
                <span className="setfieldlabel">Caller ID / from numbers</span>
                {numbers.length ? (
                  <ul className="setlist">
                    {numbers.map((n) => (
                      <li key={n.id}>{n.name}{n.numbers.length ? <em className="sub"> · {n.numbers.join(", ")}</em> : null}</li>
                    ))}
                  </ul>
                ) : <p className="sub">No Quo numbers found — they load once <code>QUO_API_KEY</code> is set and you're connected.</p>}
              </div>
              <div className="setgrid dpgrid">
                <label className="setroifield"><span className="setroilabel">Caller ID override</span>
                  <input className="setroiinput" disabled={!editable} placeholder="+1 (___) ___-____" value={dval("callerId")} onChange={(e) => setDpDraft((d) => ({ ...d, callerId: e.target.value }))} onBlur={() => commitStr("callerId")} onKeyDown={(e) => { if (e.key === "Enter") commitStr("callerId"); }} />
                </label>
                <label className="setroifield"><span className="setroilabel">Ring timeout (seconds)</span>
                  <input className="setroiinput" disabled={!editable} inputMode="numeric" value={String(dnum(D["ringTimeout"], 25))} onChange={(e) => { const n = parseInt(e.target.value, 10); if (Number.isFinite(n) && n > 0 && n <= 120) dpPut({ ringTimeout: n }); }} />
                </label>
              </div>
              <div className="setintegration">
                <div className="setintwho"><strong>Call recording</strong><span className="sub">Record outbound calls placed through Quo.</span></div>
                <button className={"dptoggle" + (dbool(D["recording"]) ? " on" : "")} disabled={!editable} onClick={() => dpPut({ recording: !dbool(D["recording"]) })}>{dbool(D["recording"]) ? "On" : "Off"}</button>
              </div>
              <div className="setintegration">
                <div className="setintwho"><strong>Voicemail drop</strong><span className="sub">Drop the pre-recorded voicemail when a call goes unanswered.</span></div>
                <button className={"dptoggle" + (dbool(D["vmDrop"]) ? " on" : "")} disabled={!editable} onClick={() => dpPut({ vmDrop: !dbool(D["vmDrop"]) })}>{dbool(D["vmDrop"]) ? "On" : "Off"}</button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="quosumrow">
      <span className="quosumlbl">{label}</span>
      <span className="quosumval">{value}</span>
    </div>
  );
}
